import { PickPublic } from '../lib/utils.ts'
import { HttpClient } from './httpClient.ts'

export interface UserTest {
  id: number
  name: string
  type: string
  access_date_from: string
  access_date_to: string
  access_time_from: string | null
  access_time_to: string | null
  allowed_attempts: number
  enabled: boolean
}

const TEST_NOT_AVAILABLE_MESSAGE = 'Test is not available at this time'

export function isTestAccessError(error: unknown): boolean {
  return error instanceof Error && error.message === TEST_NOT_AVAILABLE_MESSAGE
}

export function isTestAccessible(test: UserTest): boolean {
  if (!test.enabled || test.allowed_attempts === 0) return false

  const now = new Date()

  const fromStr = test.access_time_from
    ? `${test.access_date_from}T${test.access_time_from}`
    : `${test.access_date_from}T00:00:00`
  const fromDate = new Date(fromStr)

  const toStr = test.access_time_to
    ? `${test.access_date_to}T${test.access_time_to}`
    : `${test.access_date_to}T23:59:59`
  const toDate = new Date(toStr)
  if (test.access_time_to === '00:00:00') {
    toDate.setHours(23, 59, 59, 999)
  }

  return now >= fromDate && now <= toDate
}

export interface TestParameter {
  question: string
  options: string[]
  category: string
  check: string
}

export interface TestQuestion {
  question: string
  options: string[]
  category: string
  check: string
  correctAnswer?: string
}

export interface StartTestResponse {
  id: number
  user: number
  chat_title: string
  created: string
  program_tag: string
  language: string
  voice_language: string
  rating: number | null
  experience: number | null
  user_comment: string | null
  parameters: TestParameter[]
  next_program_tag: string | null
  mode: string
}

export interface TestValidateRequest {
  question: string
  category: string
  correct: boolean
}

export interface TestValidateResponse {
  status: 'success'
}

export type UserTestsApi = PickPublic<AppUserTestsApi>

export class AppUserTestsApi {
  constructor(private httpClient: HttpClient) {}

  async getTodayTests(courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url = '/api/tests/today/' + (params.toString() ? `?${params}` : '')
    const response = await this.httpClient.get<UserTest[]>(url)
    return response
  }

  async startTest(type: string) {
    const response = await this.httpClient.post<StartTestResponse>(
      '/api/tests/start/',
      { type },
    )
    return response
  }

  async validateTest(chatId: number, answers: TestValidateRequest[]) {
    const response = await this.httpClient.post<TestValidateResponse>(
      `/api/tests/validate/${chatId}/`,
      answers,
    )
    return response
  }
}
